"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.addANewAddressBookUriToAddressBooks = addANewAddressBookUriToAddressBooks;
exports.addAddressToTypeIndex = addAddressToTypeIndex;
exports.addContactToAddressBook = addContactToAddressBook;
exports.addWebIDToExistingContact = addWebIDToExistingContact;
exports.createContactInAddressBook = createContactInAddressBook;
exports.handleAddressBookCreation = handleAddressBookCreation;
exports.updateAddressBookName = updateAddressBookName;
var _rdflib = require("rdflib");
var _solidUi = require("solid-ui");
var _solidLogic = require("solid-logic");
var _contactsPane = _interopRequireDefault(require("contacts-pane"));
var _ContactCreationDialog = require("./ContactCreationDialog");
var _contactsErrors = require("./contactsErrors");
var _dialog = require("../../ui/dialog");
var _texts = require("../../texts");
var _selectors = require("./selectors");
var _debug = require("../../utils/debug");
var _rdfMutationHelpers = require("../../sections/shared/rdfMutationHelpers");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
async function addContactToAddressBook(context, contactsModule, contactData, addressBooksData, container) {
  const launchButton = container.querySelector('#add-to-contacts-button');
  if (launchButton) launchButton.disabled = true;
  const form = (0, _ContactCreationDialog.createAddressBookContactCreationDialog)(context, contactsModule, contactData, addressBooksData);
  const footer = (0, _ContactCreationDialog.createAddressBookContactCreationFooter)(context, contactsModule, addressBooksData, contactData);
  await (0, _dialog.openInputDialog)({
    title: 'Add contact to address book',
    dom: context.dom,
    form,
    headerAction: {
      type: 'none'
    },
    onOpen: () => {
      const buttonsContainer = context.dom.querySelector('#modal-buttons');
      if (!buttonsContainer) return;
      const sharedButtons = Array.from(buttonsContainer.querySelectorAll(':scope > solid-ui-button'));
      sharedButtons.forEach(button => {
        button.hidden = true;
        button.setAttribute('aria-hidden', 'true');
      });
      buttonsContainer.appendChild(footer);
    }
  });
  const refreshedButton = container.querySelector('#add-to-contacts-button');
  if (refreshedButton?.refresh) {
    refreshedButton.refresh();
  } else if (refreshedButton) {
    refreshedButton.disabled = false;
  }
}
async function getAddressData(context, contactsModule, addressBookUri) {
  const nextData = await (0, _selectors.getAddressBooksData)(context, contactsModule);
  const matchingAddressBookUri = resolveAddressBookUri(nextData, addressBookUri);
  const matchingAddressBook = matchingAddressBookUri ? nextData?.private.get(matchingAddressBookUri) || nextData?.public.get(matchingAddressBookUri) || null : null;
  return {
    nextData,
    matchingAddressBook,
    matchingAddressBookUri
  };
}
function getAddressBookUriCandidates(addressBookUri) {
  if (!addressBookUri) return [];
  const trimmedUri = addressBookUri.trim();
  const candidates = new Set([trimmedUri]);
  if (trimmedUri.endsWith('#this')) {
    const withoutFragment = trimmedUri.slice(0, -5);
    candidates.add(withoutFragment);
    if (!withoutFragment.endsWith('index.ttl')) {
      candidates.add(`${withoutFragment.replace(/\/?$/, '/')}index.ttl#this`.replace('/index.ttl#this', '/index.ttl#this'));
    }
  } else {
    candidates.add(`${trimmedUri}#this`);
    if (trimmedUri.endsWith('/')) {
      candidates.add(`${trimmedUri}index.ttl#this`);
    }
    if (trimmedUri.endsWith('index.ttl')) {
      candidates.add(`${trimmedUri}#this`);
    }
  }
  return Array.from(candidates);
}
function resolveAddressBookUri(addressBooksData, addressBookUri) {
  if (!addressBooksData) return null;
  const candidates = getAddressBookUriCandidates(addressBookUri);
  for (const candidate of candidates) {
    if (addressBooksData.private.has(candidate) || addressBooksData.public.has(candidate)) {
      return candidate;
    }
  }
  return null;
}
async function addANewAddressBookUriToAddressBooks(context, contactsModule, _addressBooksData, enteredAddressBookUri) {
  try {
    const {
      nextData,
      matchingAddressBook,
      matchingAddressBookUri
    } = await getAddressData(context, contactsModule, enteredAddressBookUri);
    if (!matchingAddressBook || !nextData || !matchingAddressBookUri) {
      throw new Error('Address book was not found for the entered URI.');
    }
    return {
      addressBooksData: nextData,
      addressBook: matchingAddressBook,
      addressBookUri: matchingAddressBookUri
    };
  } catch (error) {
    throw error instanceof Error ? error : new Error(String(error));
  }
}
async function addContactDetails(context, contactUri, contactData) {
  const store = context.session.store;
  const contactNode = new _rdflib.NamedNode(contactUri);
  const detailDoc = contactNode.doc();
  const max = 9999999999999;
  const min = 1000000000000;
  const createDetailNode = () => {
    const randomId = Math.floor(Math.random() * (max - min + 1)) + min;
    return (0, _rdflib.sym)(`${detailDoc.uri}#id${randomId}`);
  };
  const insertions = [];
  try {
    if (contactData.nickname) {
      insertions.push((0, _rdflib.st)(contactNode, _solidUi.ns.foaf('nick'), (0, _rdflib.literal)(contactData.nickname), detailDoc));
    }
    if (contactData.preferredSubjectPronoun) {
      insertions.push((0, _rdflib.st)(contactNode, _solidUi.ns.solid('preferredSubjectPronoun'), (0, _rdflib.literal)(contactData.preferredSubjectPronoun), detailDoc));
    }
    if (contactData.preferredObjectPronoun) {
      insertions.push((0, _rdflib.st)(contactNode, _solidUi.ns.solid('preferredObjectPronoun'), (0, _rdflib.literal)(contactData.preferredObjectPronoun), detailDoc));
    }
    if (contactData.preferredRelativePronoun) {
      insertions.push((0, _rdflib.st)(contactNode, _solidUi.ns.solid('preferredRelativePronoun'), (0, _rdflib.literal)(contactData.preferredRelativePronoun), detailDoc));
    }
    contactData.emails.map(emailInfo => {
      const node = createDetailNode();
      insertions.push((0, _rdflib.st)(contactNode, _solidUi.ns.vcard('hasEmail'), node, detailDoc));
      insertions.push((0, _rdflib.st)(node, _solidUi.ns.rdf('type'), emailInfo.type, detailDoc));
      insertions.push((0, _rdflib.st)(node, _solidUi.ns.vcard('value'), emailInfo.email, detailDoc));
    });
    contactData.phoneNumbers.map(phoneInfo => {
      const node = createDetailNode();
      insertions.push((0, _rdflib.st)(contactNode, _solidUi.ns.vcard('hasTelephone'), node, detailDoc));
      insertions.push((0, _rdflib.st)(node, _solidUi.ns.rdf('type'), phoneInfo.type, detailDoc));
      insertions.push((0, _rdflib.st)(node, _solidUi.ns.vcard('value'), phoneInfo.phoneNumber, detailDoc));
    });
    await (0, _rdfMutationHelpers.runDirectUpdaterUpdate)(store, [], insertions, _texts.errorContactCreation);
  } catch (error) {
    (0, _contactsErrors.addErrorToErrorDisplay)(context, error);
  }
}
async function createContactInAddressBook(context, contactsModule, contactData, selectedAddressBookUris) {
  let contactUri = null;
  const store = context.session.store;
  const newContact = {
    name: contactData.name
  };
  try {
    const groupUris = selectedAddressBookUris.groupUris.length ? selectedAddressBookUris.groupUris : undefined;
    if (groupUris) {
      contactUri = await contactsModule.createNewContact({
        addressBookUri: selectedAddressBookUris.addressBookUri,
        contact: newContact,
        groupUris
      });
    } else {
      (0, _contactsErrors.addErrorToErrorDisplay)(context, _texts.groupIsRequired);
      return null;
    }
    await context.session.store.fetcher.load(contactUri);
    const contactNode = new _rdflib.NamedNode(contactUri);
    await addWebIDToContactCard(store, contactNode, contactData.webID, _solidUi.ns.vcard('WebID'));
    if (contactData.emails.length || contactData.phoneNumbers.length || contactData.nickname || contactData.preferredSubjectPronoun || contactData.preferredObjectPronoun || contactData.preferredRelativePronoun) {
      await addContactDetails(context, contactUri, contactData);
    }
    return contactUri;
  } catch (error) {
    throw error instanceof Error ? error : new Error(String(error));
  }
  return null;
}
async function addAddressToTypeIndex(context, typeOfIndex, addressBookUri) {
  const store = context.session.store;
  const me = _solidLogic.authn.currentUser();
  let typeIndexNode = null;
  try {
    await store.fetcher.load(me);
    const typeIndexes = await _solidLogic.solidLogicSingleton.typeIndex.loadTypeIndexesFor(me);
    if (!typeIndexes.length) {
      throw new Error('No type index found for the current user');
    }
    typeIndexes.map(typeIndex => {
      if (typeIndex.label === typeOfIndex) typeIndexNode = typeIndex.index;
    });
    if (typeIndexNode) {
      const registration = await _solidLogic.solidLogicSingleton.typeIndex.registerInTypeIndex((0, _rdflib.sym)(addressBookUri), typeIndexNode, _solidUi.ns.vcard('AddressBook'));
      if (registration) return true;
    }
  } catch (error) {
    (0, _contactsErrors.addErrorToErrorDisplay)(context, error);
  }
  return false;
}
async function addWebIDToExistingContact(context, contactData, contactUri) {
  const store = context.session.store;
  try {
    await context.session.store.fetcher.load(contactUri);
    const contactNode = new _rdflib.NamedNode(contactUri);
    await addWebIDToContactCard(store, contactNode, contactData.webID, _solidUi.ns.vcard('WebID'));
  } catch (error) {
    const rootError = error instanceof Error ? error : new Error(String(error));
    (0, _debug.error)(_texts.addContactWebIdSaveFailedDebugText, rootError);
    throw new Error(_texts.errorAddingContactWebIDToAddressBook, {
      cause: rootError
    });
  }
}
async function updateAcrossDocuments(store, deletions, insertions = []) {
  (0, _rdfMutationHelpers.ensureStandardMutationPrefixes)(store);
  const docs = deletions.concat(insertions).map(statement => statement.why);
  const uniqueDocKeys = new Set();
  const uniqueDocs = docs.filter(doc => {
    const key = 'value' in doc ? doc.value : '__default__';
    if (uniqueDocKeys.has(key)) return false;
    uniqueDocKeys.add(key);
    return true;
  });
  await Promise.all(uniqueDocs.map(doc => (0, _rdfMutationHelpers.runDirectUpdaterUpdate)(store, deletions.filter(statement => ('value' in statement.why ? statement.why.value : '__default__') === ('value' in doc ? doc.value : '__default__')), insertions.filter(statement => ('value' in statement.why ? statement.why.value : '__default__') === ('value' in doc ? doc.value : '__default__')), _texts.errorAddingContactWebIDToAddressBook)));
}
async function addWebIDToContactCard(store, contactNode, webId, urlType) {
  try {
    new URL(webId);
  } catch {
    throw new Error(`WebID: ${webId} is not a valid url.`);
  }
  const vcardUrlNode = store.bnode();
  const insertions = [(0, _rdflib.st)(contactNode, _solidUi.ns.vcard('url'), vcardUrlNode, contactNode.doc()), (0, _rdflib.st)(vcardUrlNode, _solidUi.ns.rdf('type'), urlType, contactNode.doc()), (0, _rdflib.st)(vcardUrlNode, _solidUi.ns.vcard('value'), (0, _rdflib.literal)(webId), contactNode.doc())];
  let deletions = [];
  const groups = store.each(null, _solidUi.ns.vcard('hasMember'), contactNode);
  groups.forEach(group => {
    deletions = deletions.concat(store.statementsMatching(group, _solidUi.ns.vcard('hasMember'), contactNode, group.doc()));
    insertions.push((0, _rdflib.st)(group, _solidUi.ns.vcard('hasMember'), (0, _rdflib.sym)(webId), group.doc()));
    insertions.push((0, _rdflib.st)((0, _rdflib.sym)(webId), _solidUi.ns.owl('sameAs'), contactNode, group.doc()));
  });
  await updateAcrossDocuments(store, deletions, insertions);
}
function sanitizeAlphaNumericSpaces(input) {
  return (input || '').replace(/[^a-zA-Z0-9 ]+/g, '').replace(/\s+/g, ' ').trim();
}
async function updateAddressBookName(context, addressBookUri, newName) {
  const store = context.session.store;
  const addressBookNode = (0, _rdflib.sym)(addressBookUri);
  const addressBookDoc = addressBookNode.doc();
  const trimmedName = newName?.trim();
  if (!trimmedName) return;
  try {
    await store.fetcher.load(addressBookDoc);
    const deletions = [...store.statementsMatching(addressBookNode, _solidUi.ns.dc('title'), null, addressBookDoc), ...store.statementsMatching(addressBookNode, _solidUi.ns.vcard('fn'), null, addressBookDoc)];
    const insertions = [(0, _rdflib.st)(addressBookNode, _solidUi.ns.dc('title'), (0, _rdflib.literal)(trimmedName), addressBookDoc), (0, _rdflib.st)(addressBookNode, _solidUi.ns.vcard('fn'), (0, _rdflib.literal)(trimmedName), addressBookDoc)];
    await (0, _rdfMutationHelpers.runDirectUpdaterUpdate)(store, deletions, insertions, _texts.errorAddressBookCreation);
  } catch (error) {
    throw error instanceof Error ? error : new Error(String(error));
  }
}
async function handleAddressBookCreation(dataBrowserContext, containerName, enteredAddressName) {
  const me = _solidLogic.authn.currentUser();
  const newAddressContainer = me?.site()?.value;
  let addressBookUri = null;
  const div = dataBrowserContext.dom.getElementById('new-addressbook-form');
  try {
    if (!me || !newAddressContainer) {
      throw new Error(_texts.errorUnableToDetermineUserWorkspace);
    }
    const sanitizedAddressName = sanitizeAlphaNumericSpaces(enteredAddressName);
    if (!sanitizedAddressName) {
      throw new Error('Address book name can only contain letters, numbers, and spaces');
    }
    const sanitizedContainerName = sanitizeAlphaNumericSpaces(containerName);
    const normalizedContainer = newAddressContainer.endsWith('/') ? newAddressContainer : `${newAddressContainer}/`;
    const addressBookSlug = (sanitizedContainerName || 'address-book').trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '') || 'address-book';
    const newBase = `${normalizedContainer}${addressBookSlug}/`;
    const mintResult = await _contactsPane.default.mintNew(dataBrowserContext, {
      me,
      dom: dataBrowserContext.dom,
      div,
      newBase,
      instanceClass: _solidUi.ns.vcard('AddressBook'),
      instanceName: sanitizedAddressName
    });
    addressBookUri = mintResult?.newInstance?.uri || `${newBase}index.ttl#this`;
    await updateAddressBookName(dataBrowserContext, addressBookUri, sanitizedAddressName);
  } catch (error) {
    throw error instanceof Error ? error : new Error(String(error));
  }
  return addressBookUri;
}